import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Input,
    Label,
    ListBox,
    NumberField,
    Select,
    TextArea,
    TextField,
} from '@heroui/react';
import type {
    ChartAccount,
    LocaleCode,
    RecordableOperation,
} from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    accountOptionLabel,
    accountsForSlot,
    AMOUNT_FORMAT,
    amountToGelString,
    gelStringToAmount,
    slotLabel,
    type RecordError,
    type RecordForm as RecordFormState,
} from '../../recordMeta';

type RecordFormProps = {
    form: RecordFormState;
    operation: RecordableOperation | null;
    accounts: ChartAccount[];
    locale: LocaleCode;
    amountError: 'invalid' | null;
    formError: RecordError | null;
    isDisabled: boolean;
    onFieldChange: (patch: Partial<RecordFormState>) => void;
    onSlotChange: (lineId: string, accountId: string) => void;
};

/**
 * The generated form: an amount, one dropdown per slot, a reference and a note.
 *
 * **The date is not here.** It decides which version of the recipe applies and
 * whether the period will take a posting at all — so it leads the screen, above
 * the picker, in `components/dateBand`, and `PERIOD_CLOSED` / `NO_ACTIVE_VERSION`
 * are anchored there. A date field below the picker could retract the very
 * operation whose form it sits in.
 *
 * **The slots are the operation's own questions, in its own words.** "Which
 * expense?", "Paid from?" — the labels come from the template, not from this
 * file, which is the whole reason a non-accountant can operate it. Each dropdown
 * is narrowed by that slot's account filter, and says so underneath: *"8
 * accounts offered · filtered by the recipe"* is the difference between a short
 * list and a broken one.
 *
 * They sit under **"What the recipe asks"**, which is doing real work — it tells
 * the user the fields below vary because the *recipe* says so, not because the
 * app is inconsistent.
 *
 * The helper text makes **no branch claim**. Accounts are not branch-scoped and
 * nothing filters by branch; the design's "your branch only" would be describing
 * a rule that does not exist.
 *
 * A slot error is rendered once, under the slots, rather than on a dropdown: the
 * server names the offending account and label in prose (`SLOT_OUTSIDE_FILTER`
 * quotes both), and picking which of four dropdowns to decorate from that string
 * would mean parsing it. The message names the slot; the anchor gets the reader
 * to the right group.
 */
export const RecordForm: React.FC<RecordFormProps> = ({
    form,
    operation,
    accounts,
    locale,
    amountError,
    formError,
    isDisabled,
    onFieldChange,
    onSlotChange,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.form.title')}
            className="flex flex-col gap-4 rounded-2xl border border-separator bg-surface p-4"
            {...getNodeTestIdProp('operations-record-form')}
        >
            <NumberField
                className="flex flex-col gap-1"
                formatOptions={AMOUNT_FORMAT}
                isDisabled={isDisabled}
                isInvalid={
                    amountError !== null || formError?.target === 'amount'
                }
                // A recorded amount is a magnitude: direction lives in the
                // recipe, so a negative here is a mistake rather than "the
                // other way round". The server refuses it too
                // (`AMOUNT_NOT_POSITIVE`); this stops it being typed.
                minValue={0}
                onChange={(next) =>
                    onFieldChange({ amountGel: amountToGelString(next) })
                }
                value={gelStringToAmount(form.amountGel)}
                // Subdued, because this field sits on the form's own raised
                // card — the same reason the template editor's sample field is
                // secondary inside its Widget.
                variant="secondary"
                {...getNodeTestIdProp('operations-record-amount')}
            >
                <Label className="text-xs text-muted">
                    {t('operations.record.form.amount')}
                </Label>
                {/* The template editor's sample-amount field, part for part.
                    Both steppers are structural rather than decorative:
                    `.number-field__group` is a grid of `40px 1fr 40px` built
                    for decrement / input / increment, so dropping them drops
                    the input into the first 40px column and clips the value to
                    about three characters.

                    `w-72` rather than that field's `w-44`: 176px is the width
                    that clips `₾9,000,000.00` once the two buttons have taken
                    their 80px, which is the bug this field was opened for. */}
                <NumberField.Group className="w-72 max-w-full">
                    <NumberField.DecrementButton />
                    <NumberField.Input
                        className="text-center text-sm font-semibold tabular-nums"
                        placeholder="0.00"
                    />
                    <NumberField.IncrementButton />
                </NumberField.Group>
            </NumberField>

            {amountError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('operations-record-amount-error')}
                >
                    {t('operations.record.form.amountInvalid')}
                </p>
            ) : null}

            {formError && formError.target === 'amount' ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp(
                        'operations-record-amount-server-error',
                    )}
                >
                    {formError.message}
                </p>
            ) : null}

            {operation && operation.slots.length > 0 ? (
                <div
                    className="flex flex-col gap-3"
                    {...getNodeTestIdProp('operations-record-slots')}
                >
                    <span className="text-xs font-medium text-foreground">
                        {t('operations.record.form.whatTheRecipeAsks')}
                    </span>
                    {operation.slots.map((slot) => (
                        <div className="flex flex-col gap-1" key={slot.lineId}>
                            <Select
                                className="w-full"
                                isDisabled={isDisabled}
                                onChange={(next) =>
                                    next &&
                                    onSlotChange(slot.lineId, String(next))
                                }
                                placeholder={t(
                                    'operations.record.form.slotPlaceholder',
                                )}
                                value={form.slots[slot.lineId] ?? null}
                            >
                                <Label className="text-xs text-muted">
                                    {slotLabel(slot, locale)}
                                </Label>
                                <Select.Trigger
                                    {...getNodeTestIdProp(
                                        `operations-record-slot-${slot.lineId}`,
                                    )}
                                >
                                    <Select.Value />
                                    <Select.Indicator />
                                </Select.Trigger>
                                <Select.Popover>
                                    <ListBox>
                                        {accountsForSlot(accounts, slot).map(
                                            (account) => (
                                                <ListBox.Item
                                                    id={account.id}
                                                    key={account.id}
                                                    textValue={accountOptionLabel(
                                                        account,
                                                    )}
                                                >
                                                    {accountOptionLabel(
                                                        account,
                                                    )}
                                                    <ListBox.ItemIndicator />
                                                </ListBox.Item>
                                            ),
                                        )}
                                    </ListBox>
                                </Select.Popover>
                            </Select>
                            <span
                                className="text-xs text-muted"
                                {...getNodeTestIdProp(
                                    `operations-record-slot-offered-${slot.lineId}`,
                                )}
                            >
                                {t('operations.record.form.slotOffered', {
                                    count: accountsForSlot(accounts, slot)
                                        .length,
                                })}
                            </span>
                        </div>
                    ))}

                    {formError && formError.target === 'slots' ? (
                        <p
                            className="text-sm text-danger"
                            {...getNodeTestIdProp(
                                'operations-record-slots-error',
                            )}
                        >
                            {formError.message}
                        </p>
                    ) : null}
                </div>
            ) : null}

            <div className="flex flex-col gap-3">
                <TextField
                    className="flex w-full flex-col gap-1"
                    isDisabled={isDisabled}
                    onChange={(value) => onFieldChange({ reference: value })}
                    value={form.reference}
                    {...getNodeTestIdProp('operations-record-reference')}
                >
                    <Label className="text-xs text-muted">
                        {t('operations.record.form.referenceOptional')}
                    </Label>
                    <Input
                        placeholder={t(
                            'operations.record.form.referencePlaceholder',
                        )}
                    />
                </TextField>

                <TextField
                    className="flex w-full flex-col gap-1"
                    isDisabled={isDisabled}
                    onChange={(value) => onFieldChange({ note: value })}
                    value={form.note}
                    {...getNodeTestIdProp('operations-record-note')}
                >
                    <Label className="text-xs text-muted">
                        {t('operations.record.form.noteOptional')}
                    </Label>
                    <TextArea rows={2} />
                </TextField>
            </div>
        </section>
    );
};
